import { Avatar, type MantineRadius } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSignedImageUrl } from "../../hooks/useSignedImageUrl";
import { ZoomModal } from "./ZoomModal";

/**
 * Avatar for images stored in the private assets bucket.
 *
 * Passing a raw S3 key straight to <Avatar src> silently fails — the bucket
 * is private, so the browser gets a 403 and Mantine falls back to the initial.
 * Every profile photo in the admin needs the same presigned GET the KYC cards
 * already use. Clicking a real photo opens the interactive pan/zoom viewer;
 * pass `zoomable={false}` to opt an instance out (e.g. the nav-bar avatar).
 */
export function S3Avatar({
  src,
  size = 40,
  radius = "xl",
  color = "safiri",
  children,
  alt,
  zoomable = true,
}: {
  src?: string | null;
  size?: number | string;
  radius?: MantineRadius;
  color?: string;
  children?: React.ReactNode;
  alt?: string;
  zoomable?: boolean;
}) {
  const { url } = useSignedImageUrl(src);
  const [opened, { open, close }] = useDisclosure(false);
  const canZoom = zoomable && !!url;

  return (
    <>
      <Avatar
        src={url ?? undefined}
        size={size}
        radius={radius}
        color={color}
        onClick={canZoom ? open : undefined}
        style={canZoom ? { cursor: "zoom-in" } : undefined}
      >
        {children}
      </Avatar>
      {canZoom && <ZoomModal opened={opened} onClose={close} src={url} alt={alt} title={alt} />}
    </>
  );
}
